import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";

import Table from "../../../components/table";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { getActiveReviewSessions, endReviewSession } from "../../../services/admin/reviewSessions";
import adminStyles from "../../../styles/caseManagerAdmin.module.css";
import search from "../../../assets/images/search.png";
import styles from "./ActiveReviewSessionsTab.module.css";

const REFRESH_MS = 30 * 1000;

// Reviewer working the session; for a paused one, whoever last worked it.
const reviewerOf = (s) => s?.locked_by?.name || s?.paused_by?.name || null;
const isPaused = (s) => s?.status === "paused";

// "02:15 Hrs" - time since the reviewer's current turn started.
const formatElapsed = (since, now) => {
  const start = since ? new Date(since).getTime() : NaN;
  if (Number.isNaN(start)) return "—";
  const mins = Math.max(0, Math.floor((now - start) / 60000));
  return `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")} Hrs`;
};

// SCRUM-962: Admin › Active Review Sessions - the review sessions left open
// in this account (active and paused), with End Session. Per Figma: Care
// Manager = whose patients are being reviewed; CM Comp = the reviewer
// working the session (last reviewer, for a paused one).
const ActiveReviewSessionsTab = () => {
  const queryClient = useQueryClient();
  const scope = sessionStorage.getItem("schema_name");
  const [nameSearch, setNameSearch] = useState("");
  const [confirming, setConfirming] = useState(null); // session row
  const [now, setNow] = useState(Date.now());

  // Keeps "Time in Current Session" moving between refreshes.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), REFRESH_MS);
    return () => clearInterval(t);
  }, []);

  const queryKey = ["admin", "active-review-sessions", scope];
  const { data: sessions = [], isLoading } = useQuery(queryKey, () => getActiveReviewSessions(scope), {
    refetchInterval: REFRESH_MS,
    staleTime: 0,
    onError: () => toast.error("Could not load active review sessions."),
  });

  const endMutation = useMutation(({ id, account_id }) => endReviewSession(id, account_id), {
    onSuccess: (_, row) => {
      toast.success(
        reviewerOf(row) ? `Ended ${reviewerOf(row)}'s review session.` : "Review session ended."
      );
      // Drop it from the list straight away, then refresh.
      queryClient.setQueryData(queryKey, (prev = []) => prev.filter((s) => s.id !== row.id));
      queryClient.invalidateQueries(queryKey);
      setConfirming(null);
    },
    onError: (error) => {
      const status = error?.response?.status;
      if (status === 403) toast.error("You don't have permission to end review sessions.");
      else if (status === 409) {
        toast.error("This review session has already ended.");
        queryClient.invalidateQueries(queryKey);
        setConfirming(null);
      } else toast.error(error?.response?.data?.detail || "Could not end the review session.");
    },
  });

  const rows = useMemo(() => {
    const q = nameSearch.trim().toLowerCase();
    if (!q) return sessions;
    return sessions.filter((s) =>
      [s.case_manager?.name, reviewerOf(s)].some((n) => (n || "").toLowerCase().includes(q))
    );
  }, [sessions, nameSearch]);

  const columns = [
    { key: "care_manager", label: "Care Manager", alignLeft: true, render: (s) => s.case_manager?.name || "—" },
    { key: "cm_comp", label: "CM Comp", alignLeft: true, render: (s) => reviewerOf(s) || "—" },
    { key: "account", label: "Account", alignLeft: true, render: (s) => s.account || "—" },
    {
      key: "time",
      label: "Time in Current Session",
      alignLeft: true,
      // Nobody is in a paused session, so there's no current time to show.
      render: (s) =>
        isPaused(s) ? <span className={styles.pausedTag}>Paused</span> : formatElapsed(s.locked_since, now),
    },
    {
      key: "patients",
      label: "Patients Completed",
      alignLeft: true,
      render: (s) => `${s.completed_patients ?? 0}/${s.total_patients ?? 0}`,
    },
    {
      key: "readings",
      label: "Readings reviewed",
      alignLeft: true,
      // Not in the API's open_sessions yet - shown once the backend adds it.
      render: (s) => s.readings_reviewed ?? "—",
    },
    {
      key: "actions",
      label: "Actions",
      sorting: false,
      render: (s) => (
        <button type="button" className={styles.endBtn} onClick={() => setConfirming(s)}>
          End Session
        </button>
      ),
    },
  ];

  const reviewer = reviewerOf(confirming) || "This reviewer";
  const caseManager = confirming?.case_manager?.name || "this care manager";
  const confirmingPaused = isPaused(confirming);

  return (
    <div className={adminStyles.table_container}>
      <div className={`${adminStyles.searchbar_container} ${adminStyles.location_search}`}>
        <div className={adminStyles.input_wrapper}>
          <input
            type="text"
            placeholder="Search by Name"
            value={nameSearch}
            onChange={(e) => setNameSearch(e.target.value)}
          />
          <img alt=" " src={search} />
        </div>
      </div>

      <div className={adminStyles.all_patients}>
        <Table columns={columns} data={rows} loading={isLoading} />
      </div>

      {/* Figma "End Current Session" (7951:2429) */}
      <Dialog open={!!confirming} setOpen={(o) => !o && !endMutation.isLoading && setConfirming(null)}>
        <DialogContent style={{ maxWidth: 451 }}>
          <DialogHeader>
            <DialogTitle>End Current Session for {reviewer}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className={styles.confirmText}>
              {confirmingPaused ? (
                <>
                  <strong>{reviewer}</strong> was reviewing patients for <strong>{caseManager}</strong>. This
                  session is paused.
                </>
              ) : (
                <>
                  <strong>{reviewer}</strong> is reviewing patients for <strong>{caseManager}</strong>.
                </>
              )}
            </p>
            <p className={styles.confirmText}>Are you sure you want to end this review session?</p>
          </DialogBody>
          <DialogFooter className={styles.confirmFooter}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={() => setConfirming(null)}
              disabled={endMutation.isLoading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.confirmEndBtn}
              onClick={() => endMutation.mutate(confirming)}
              disabled={endMutation.isLoading}
            >
              {endMutation.isLoading ? "Ending…" : "End Session"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActiveReviewSessionsTab;
